import { Loadable } from '@/components/loaders';
import { mapCampaignStatus, STATUS_LABEL_KEYS, STATUS_STYLES } from '@/features/campaigns/v2/Whatsapp/Campaigns/campaignStatus';
import { COMUNI_TABS_ITEM } from '@/features/comuni/comuni-helper';
import RouteConstants from '@/features/router/RouteConstants';
import { Button, IconButton, PlusIcon } from '@/lib';
import { Card } from '@/lib/Layout/Card';
import { addDays, eachDayOfInterval, endOfWeek, format, isSameDay, startOfMonth, startOfWeek } from 'date-fns';
import { enUS, it } from 'date-fns/locale';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import {
  buildMonthDays,
  campaignCalendarDate,
  campaignDeliveryRate,
  campaignDotKind,
  campaignsOnDay,
  CalendarCampaign,
  CalendarDayCell,
  selectedDayInMonth,
  shiftVisibleMonth,
} from './communicationsCalendarData';
import { useCalendarCampaigns } from './useCalendarCampaigns';

const newCommunicationPath = `${RouteConstants.comuni}?tab=${COMUNI_TABS_ITEM.NEW_COMMUNICATION}`;

const campaignDetailsPath = (campaign: CalendarCampaign) => {
  const route =
    campaign.providerType === 'SMS'
      ? RouteConstants.smsCampaignDetails
      : campaign.providerType === 'EMAIL'
        ? RouteConstants.emailCampaignDetails
        : RouteConstants.whatsappCampaignDetails;

  return route.replace(':campaignId', campaign.id);
};

const providerLabelKey = (providerType: string) => {
  if (providerType === 'SMS') return 'campaign:tabs.sms';
  if (providerType === 'EMAIL') return 'campaign:tabs.email';
  return 'campaign:tabs.whatsapp';
};

export const CommunicationsCalendar: FC = () => {
  const { t, i18n } = useTranslation();
  const { campaigns, isLoading, isError, refetch } = useCalendarCampaigns();
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState(() => new Date());
  const today = new Date();
  const locale = i18n.language.startsWith('it') ? it : enUS;
  const days = buildMonthDays(visibleMonth, selected, today, campaigns);
  const items = campaignsOnDay(campaigns, selected);
  const loadFailedCompletely = isError && campaigns.length === 0;

  const showMonth = (direction: 1 | -1) => {
    const nextMonth = shiftVisibleMonth(visibleMonth, direction);
    setVisibleMonth(nextMonth);
    setSelected((current) => selectedDayInMonth(current, nextMonth));
  };
  const weekdays = eachDayOfInterval({
    start: startOfWeek(today, { weekStartsOn: 1 }),
    end: endOfWeek(today, { weekStartsOn: 1 }),
  }).map((day) => format(day, 'EEE', { locale }));

  const dayButtonLabel = (day: CalendarDayCell) => {
    const date = format(day.date, 'PPPP', { locale });
    if (day.hasScheduled && day.hasSent) return t('home:calendarDayScheduledAndSent', { date });
    if (day.hasScheduled) return t('home:calendarDayScheduled', { date });
    if (day.hasSent) return t('home:calendarDaySent', { date });
    return t('home:calendarDay', { date });
  };

  const selectedTitle = (() => {
    if (isSameDay(selected, today)) return t('home:calendarToday');
    if (isSameDay(selected, addDays(today, 1))) return t('home:calendarTomorrow');
    const date = format(selected, 'MMMM d', { locale });
    return selected < today ? t('home:calendarSentOn', { date }) : t('home:calendarScheduledFor', { date });
  })();

  return (
    <section>
      <div className='flex flex-wrap items-end justify-between gap-4'>
        <h2 className='text-2xl font-bold text-quaternary-900'>{t('home:calendarTitle')}</h2>
        <div className='flex items-center gap-4 text-sm text-quaternary-700'>
          <span className='inline-flex items-center gap-2'>
            <span className='size-2 rounded-full bg-brand-600' />
            {t('home:calendarScheduled')}
          </span>
          <span className='inline-flex items-center gap-2'>
            <span className='size-2 rounded-full bg-success-600' />
            {t('home:calendarSent')}
          </span>
        </div>
      </div>
      <Card className='mt-3.5 w-full flex-col overflow-hidden p-0'>
        <Loadable isLoading={isLoading} className='min-h-64'>
          <div className='grid grid-cols-1 lg:grid-cols-[25rem_minmax(0,1fr)]'>
            <div className='border-b border-gray-200 p-6 lg:border-b-0 lg:border-r'>
              <div className='flex items-center justify-between gap-2'>
                <IconButton
                  intent='tertiary'
                  size='sm'
                  aria-label={t('home:calendarPreviousMonth')}
                  leadingIcon={<LuChevronLeft className='size-5' />}
                  onClick={() => showMonth(-1)}
                />
                <p className='text-center text-base font-bold text-quaternary-900'>{format(visibleMonth, 'MMMM yyyy', { locale })}</p>
                <IconButton
                  intent='tertiary'
                  size='sm'
                  aria-label={t('home:calendarNextMonth')}
                  leadingIcon={<LuChevronRight className='size-5' />}
                  onClick={() => showMonth(1)}
                />
              </div>
              <div className='mt-4 grid grid-cols-7 text-center text-xs text-quaternary-700'>
                {weekdays.map((weekday) => (
                  <span key={weekday}>{weekday}</span>
                ))}
              </div>
              <div className='mt-2.5 grid grid-cols-7 gap-y-1.5'>
                {days.map((day) => (
                  <div key={day.date.toISOString()} className='flex flex-col items-center gap-1'>
                    {day.inMonth ? (
                      <button
                        type='button'
                        aria-pressed={day.isSelected}
                        aria-label={dayButtonLabel(day)}
                        onClick={() => setSelected(day.date)}
                        className={twMerge(
                          'flex size-9 items-center justify-center rounded-lg text-sm text-primary-900',
                          day.isSelected && 'bg-brand-600 font-bold text-white-100',
                          day.isToday && !day.isSelected && 'border border-primary-900 font-bold'
                        )}
                      >
                        {day.day}
                      </button>
                    ) : (
                      <span className='flex size-9 items-center justify-center text-sm text-quaternary-400'>{day.day}</span>
                    )}
                    <span className='flex h-1.5 gap-0.5' aria-hidden='true'>
                      {day.hasScheduled ? <span className='size-1.5 rounded-full bg-brand-600' /> : null}
                      {day.hasSent ? <span className='size-1.5 rounded-full bg-success-600' /> : null}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className='relative flex min-h-0 flex-col'>
              <div className='flex max-h-80 min-h-0 flex-col bg-secondary-background lg:absolute lg:inset-0 lg:max-h-none'>
                <div className='flex shrink-0 flex-wrap items-center justify-between gap-3 p-6 pb-0'>
                  <h3 className='text-base font-bold text-quaternary-900'>{selectedTitle}</h3>
                  <Link to={newCommunicationPath}>
                    <IconButton size='md' text={t('home:calendarScheduleMessage')} leadingIcon={<PlusIcon className='size-5' />} />
                  </Link>
                </div>
                {isError ? (
                  <div className='mt-4 flex shrink-0 flex-col items-start gap-3 px-6'>
                    <p className='text-sm text-quaternary-700'>{t('home:calendarLoadError')}</p>
                    <Button intent='secondary' size='md' text={t('home:calendarRetry')} onClick={() => refetch()} />
                  </div>
                ) : null}
                <div className='mt-4 flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-6 pb-6'>
                  {loadFailedCompletely ? null : items.length === 0 ? (
                    <div className='flex flex-1 flex-col items-center justify-center px-5 py-10 text-center text-quaternary-700'>
                      <p className='text-sm font-semibold'>{t('home:calendarEmptyTitle')}</p>
                      <p className='mt-1.5 text-sm'>{t('home:calendarEmptyDescription')}</p>
                    </div>
                  ) : (
                    <ul className='flex flex-col gap-3'>
                      {items.map((campaign) => {
                        const status =
                          campaign.status === 'ACTIVE' && campaignDotKind(campaign, today) === 'scheduled' ? 'scheduled' : mapCampaignStatus(campaign.status);
                        const deliveryRate = campaignDeliveryRate(campaign, today);
                        const provider = t(providerLabelKey(campaign.providerType));

                        return (
                          <li key={campaign.id}>
                            <Link to={campaignDetailsPath(campaign)} className='flex items-center gap-4 rounded-xl border border-gray-200 bg-white-100 p-4'>
                              <p className='w-14 shrink-0 text-center text-base font-bold text-quaternary-900'>
                                {format(campaignCalendarDate(campaign), 'HH:mm', { locale })}
                              </p>
                              <div className='min-w-0 flex-1'>
                                <p className='truncate text-base font-bold text-quaternary-900'>{campaign.name}</p>
                                <p className='mt-1 text-sm text-quaternary-700'>{deliveryRate ? `${provider} · ${deliveryRate}` : provider}</p>
                              </div>
                              <span className={twMerge('inline-flex shrink-0 items-center rounded-full px-3 py-1 text-xs font-bold', STATUS_STYLES[status])}>
                                {t(STATUS_LABEL_KEYS[status])}
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Loadable>
      </Card>
    </section>
  );
};
